import { useMemo } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { parsePSQLIntervalToDays } from '../utils/task-interval-utils'
import { TaskCalendarTaskBar } from './task-calendar-task-bar'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * Build the list of weeks for a given month.
 * Each week is an array of 7 Date objects (null = padding from prev/next month).
 */
const buildWeeks = (year, month) => {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const weeks = []

  let current = new Date(firstDay)
  // Pad to Sunday
  current.setDate(current.getDate() - current.getDay())

  while (current <= lastDay || current.getDay() !== 0) {
    const week = []
    for (let i = 0; i < 7; i++) {
      week.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }
    weeks.push(week)
    if (current > lastDay && current.getDay() === 0) break
  }
  return weeks
}

/**
 * For a task, compute which column (1-7, Sun=1) and how many cells it occupies
 * within a given week. Returns null if the task doesn't overlap this week.
 */
const getTaskBarSegment = (task, weekStart, weekEnd) => {
  const taskStart = new Date(task.task_window_start)
  const durationDays = parsePSQLIntervalToDays(task.duration)
  // End date is exclusive (last day the task is still active)
  const taskEnd = new Date(taskStart)
  taskEnd.setDate(taskEnd.getDate() + durationDays - 1)

  // No overlap
  if (taskEnd < weekStart || taskStart > weekEnd) return null

  const clampedStart = taskStart < weekStart ? weekStart : taskStart
  const clampedEnd = taskEnd > weekEnd ? weekEnd : taskEnd

  // startCol is 1-indexed (Sun=1)
  const startCol = clampedStart.getDay() + 1
  const span = Math.round((clampedEnd - clampedStart) / 86400000) + 1

  return { startCol, span }
}

export const TaskCalendarMonth = ({ year, month, tasks }) => {
  const theme = useTheme()
  const weeks = useMemo(() => buildWeeks(year, month), [year, month])
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Day-of-week header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          mb: 0.5,
          flexShrink: 0,
        }}
      >
        {DAY_LABELS.map(label => (
          <Typography
            key={label}
            variant="caption"
            align="center"
            sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem' }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {/* Week rows */}
      {weeks.map((week, weekIdx) => {
        const weekStart = new Date(week[0])
        weekStart.setHours(0, 0, 0, 0)
        const weekEnd = new Date(week[6])
        weekEnd.setHours(23, 59, 59, 999)

        // Compute task bar segments for this week
        const segments = tasks
          .map(task => {
            const seg = getTaskBarSegment(task, weekStart, weekEnd)
            return seg ? { task, ...seg } : null
          })
          .filter(Boolean)

        const todayColIdx = week.findIndex(d => d.getTime() === today.getTime())

        return (
          <Box
            key={weekIdx}
            sx={{
              position: 'relative',
              flex: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Today column — full-height overlay behind day numbers and task bars */}
            {todayColIdx !== -1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${(todayColIdx / 7) * 100}%`,
                  width: `${100 / 7}%`,
                  border: `1px solid ${theme.palette.primary.main}`,
                  backgroundColor: `${theme.palette.primary.main}18`,
                  borderRadius: '3px',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
            )}

            {/* Day number row */}
            <Box sx={{ position: 'relative', zIndex: 1, flexShrink: 0, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {week.map((day, dayIdx) => {
                const isCurrentMonth = day.getMonth() === month
                const isToday = day.getTime() === today.getTime()
                return (
                  <Box
                    key={dayIdx}
                    sx={{ display: 'flex', justifyContent: 'flex-end', pr: 0.5, pt: 0.25 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        lineHeight: 1.4,
                        color: isCurrentMonth
                          ? isToday ? 'primary.main' : 'text.primary'
                          : 'text.disabled',
                        fontWeight: isToday ? 700 : 400,
                        fontSize: '0.7rem',
                      }}
                    >
                      {day.getDate()}
                    </Typography>
                  </Box>
                )
              })}
            </Box>

            {/* Task bars for this week — scrollable when tasks overflow the row */}
            {segments.length > 0 && (
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  alignContent: 'start',
                  px: 0.25,
                  pb: 0.25,
                }}
              >
                {segments.map(({ task, startCol, span }) => (
                  <TaskCalendarTaskBar
                    key={task.id}
                    task={task}
                    startCol={startCol}
                    span={span}
                  />
                ))}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
